import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { immediateToast } from "izitoast-react";
import {
  updateExpense,
  UpdateExpenseRequest,
  toggleExpenseStatus,
  ToggleExpenseStatusRequest,
  getExpenseDetail,
  getExpenseParticipants,
  connectUserToExpense,
  ConnectUserToExpenseRequest,
  removeUserFromExpense,
  RemoveUserFromExpenseRequest,
  GroupWalletTransferRequest,
  groupWalletTransfer
} from "src/network/graphql/expenseService";
import { getGroupDetail, getGroupUser } from "src/network/graphql/groupService";
import {
  getAllTransaction,
  TransactionRequest,
  listNote,
  createNote,
  saveActivityPreference,
  SaveActivityPreferenceRequest,
  CreateNoteRequest
} from "src/network/graphql/transactionService";
import { Expense } from "../expense/expenseSlice";
import { GroupUser, GroupWallet } from "../groupDetail/groupDetailSlice";
import { Note, Transaction } from "../transactions/transactionSlice";
import { searchWallet, SearchWalletRequest } from "src/network/graphql/walletService";
import { TransferRow } from "src/components/expenseComponent/transactionsCard/WalletTransferModal";
import { searchUser, SearchUserRequest } from "src/network/graphql/userService";
import { formatFullName } from "src/utils";

export type UserWalletState = {
  list: GroupWallet[];
  loading: boolean;
};

const expenseDetailSlice = createSlice({
  name: "expenseDetail",
  initialState: {
    loading: false,
    error: null,
    expenseDetail: {} as Expense,
    participantsLoading: false,
    transactionsLoading: false,
    groupWalletsLoading: false,
    userLoading: false,
    participants: {
      list: [] as GroupUser[],
      total: 0
    },
    transactions: {
      list: [] as Transaction[],
      total: 0
    },
    expenseUserLoading: false,
    expenseUser: {
      list: [] as GroupUser[],
      total: 0
    },
    notesLoading: false,
    expenseNotes: {
      list: [] as Note[],
      total: 0
    },
    groupWallets: {
      list: [] as GroupWallet[],
      total: 0
    },
    userWallets: {} as Record<string, UserWalletState>,
    users: {
      list: [],
      total: 0
    }
  },
  reducers: {
    fetchExpenseDetailStart: (state) => {
      state.loading = true;
    },
    fetchExpenseDetailSuccess: (state, action) => {
      state.loading = false;
      state.expenseDetail = action.payload ?? {};
    },
    fetchExpenseDetailFail: (state, action) => {
      state.loading = false;
      state.expenseDetail = {} as Expense;
      state.error = action.payload;
    },
    updateExpenseStart: (state) => {
      state.loading = true;
    },
    updateExpenseSuccess: (state) => {
      state.loading = false;
    },
    updateExpenseFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    toggleExpenseStatusStart: (state) => {
      state.loading = true;
    },
    toggleExpenseStatusSuccess: (state, action) => {
      state.loading = false;
      state.expenseDetail.status = action.payload;
    },
    toggleExpenseStatusFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchParticipantsStart: (state) => {
      state.participantsLoading = true;
    },
    fetchParticipantsSuccess: (state, action) => {
      state.participantsLoading = false;
      state.participants.list = action.payload?.list ?? [];
      state.participants.total = action.payload?.total ?? 0;
    },
    fetchParticipantsFail: (state, action) => {
      state.participantsLoading = false;
      state.participants.list = [];
      state.participants.total = 0;
      state.error = action.payload;
    },
    fetchTransactionsStart: (state) => {
      state.transactionsLoading = true;
    },
    fetchTransactionsSuccess: (state, action) => {
      state.transactionsLoading = false;
      state.transactions.list = action.payload?.list ?? [];
      state.transactions.total = action.payload?.total ?? 0;
    },
    fetchTransactionsFail: (state, action) => {
      state.transactionsLoading = false;
      state.transactions.list = [];
      state.transactions.total = 0;
      state.error = action.payload;
    },
    connectUserToExpenseStart: (state) => {
      state.expenseUserLoading = true;
    },
    connectUserToExpenseSuccess: (state) => {
      state.expenseUserLoading = false;
    },
    connectUserToExpenseFail: (state, action) => {
      state.expenseUserLoading = false;
      state.error = action.payload;
    },
    removeUserFromExpenseStart: (state) => {
      state.expenseUserLoading = true;
    },
    removeUserFromExpenseSuccess: (state) => {
      state.expenseUserLoading = false;
    },
    removeUserFromExpenseFail: (state, action) => {
      state.expenseUserLoading = false;
      state.error = action.payload;
    },
    fetchExpenseParticipantsStart: (state) => {
      state.expenseUserLoading = true;
    },
    fetchExpenseParticipantsSuccess: (state, action) => {
      state.expenseUserLoading = false;
      state.expenseUser.list = action.payload?.list ?? [];
      state.expenseUser.total = action.payload?.total ?? 0;
    },
    fetchExpenseParticipantsFail: (state, action) => {
      state.expenseUserLoading = false;
      state.expenseUser.list = [];
      state.expenseUser.total = 0;
      state.error = action.payload;
    },
    fetchExpenseNotesStart: (state) => {
      state.notesLoading = true;
    },
    fetchExpenseNotesSuccess: (state, action) => {
      state.notesLoading = false;
      state.expenseNotes.list = action.payload?.list ?? [];
      state.expenseNotes.total = action.payload?.total ?? 0;
    },
    fetchExpenseNotesFail: (state, action) => {
      state.notesLoading = false;
      state.expenseNotes.list = [];
      state.expenseNotes.total = 0;
      state.error = action.payload;
    },
    createExpenseNoteStart: (state) => {
      state.notesLoading = true;
    },
    createExpenseNoteSuccess: (state) => {
      state.notesLoading = false;
    },
    createExpenseNoteFail: (state, action) => {
      state.notesLoading = false;
      state.error = action.payload;
    },
    fetchGroupWalletsStart: (state) => {
      state.groupWalletsLoading = true;
    },
    fetchGroupWalletsSuccess: (state, action) => {
      state.groupWalletsLoading = false;
      state.groupWallets.list = action.payload?.list ?? [];
      state.groupWallets.total = action.payload?.total ?? 0;
    },
    fetchGroupWalletsFail: (state, action) => {
      state.groupWalletsLoading = false;
      state.groupWallets.list = [];
      state.groupWallets.total = 0;
      state.error = action.payload;
    },
    fetchUserWalletsStart: (state, action) => {
      const userId: string = action.payload;

      state.userWallets[userId] = {
        list: state.userWallets[userId]?.list ?? [],
        loading: true
      };
    },
    fetchUserWalletsSuccess: (state, action) => {
      const { userId, list } = action.payload;

      state.userWallets[userId] = {
        list: list ?? [],
        loading: false
      };
    },
    fetchUserWalletsFail: (state, action) => {
      const { userId, error } = action.payload;

      state.userWallets[userId] = {
        list: [],
        loading: false
      };
      state.error = error;
    },
    groupWalletTransferStart: (state) => {
      state.transactionsLoading = true;
    },
    groupWalletTransferSuccess: (state) => {
      state.transactionsLoading = false;
    },
    groupWalletTransferFail: (state, action) => {
      state.transactionsLoading = false;
      state.error = action.payload;
    },
    fetchUsersStart: (state) => {
      state.userLoading = true;
    },
    fetchUsersSuccess: (state, action) => {
      state.userLoading = false;
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchUsersFail: (state, action) => {
      state.userLoading = false;
      state.users.list = [];
      state.users.total = 0;
      state.error = action.payload;
    },
    saveActivityPreferenceStart: (state) => {
      state.transactionsLoading = true;
    },
    saveActivityPreferenceSuccess: (state) => {
      state.transactionsLoading = false;
    },
    saveActivityPreferenceFail: (state, action) => {
      state.transactionsLoading = false;
      state.error = action.payload;
    }
  }
});

const {
  fetchExpenseDetailStart,
  fetchExpenseDetailSuccess,
  fetchExpenseDetailFail,
  updateExpenseStart,
  updateExpenseSuccess,
  updateExpenseFail,
  toggleExpenseStatusStart,
  toggleExpenseStatusSuccess,
  toggleExpenseStatusFail,
  fetchParticipantsStart,
  fetchParticipantsSuccess,
  fetchParticipantsFail,
  fetchTransactionsStart,
  fetchTransactionsSuccess,
  fetchTransactionsFail,
  connectUserToExpenseStart,
  connectUserToExpenseSuccess,
  connectUserToExpenseFail,
  removeUserFromExpenseStart,
  removeUserFromExpenseSuccess,
  removeUserFromExpenseFail,
  fetchExpenseParticipantsStart,
  fetchExpenseParticipantsSuccess,
  fetchExpenseParticipantsFail,
  fetchExpenseNotesStart,
  fetchExpenseNotesSuccess,
  fetchExpenseNotesFail,
  createExpenseNoteStart,
  createExpenseNoteSuccess,
  createExpenseNoteFail,
  fetchGroupWalletsStart,
  fetchGroupWalletsSuccess,
  fetchGroupWalletsFail,
  fetchUserWalletsStart,
  fetchUserWalletsSuccess,
  fetchUserWalletsFail,
  groupWalletTransferStart,
  groupWalletTransferSuccess,
  groupWalletTransferFail,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  saveActivityPreferenceStart,
  saveActivityPreferenceSuccess,
  saveActivityPreferenceFail
} = expenseDetailSlice.actions;

export const fetchExpenseDetailAction = (expenseId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchExpenseDetailStart());
    try {
      const response = await getExpenseDetail(expenseId);

      dispatch(fetchExpenseDetailSuccess(response.data.get_expense));
    } catch (error) {
      dispatch(fetchExpenseDetailFail(error));
    }
  };
};

export const updateExpenseAction = (data: UpdateExpenseRequest, onSuccess?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateExpenseStart());
    try {
      await updateExpense(data);

      dispatch(updateExpenseSuccess());
      immediateToast("success", {
        message: "Claim updated successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(updateExpenseFail(error));
    }
  };
};

export const toggleExpenseStatusAction = (request: ToggleExpenseStatusRequest, onSuccess?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(toggleExpenseStatusStart());
    try {
      await toggleExpenseStatus(request);

      const newStatus = request.is_active ? "ACTIVE" : "INACTIVE";

      dispatch(toggleExpenseStatusSuccess(newStatus));
      immediateToast("success", {
        message: `Claim status updated to ${newStatus.toLowerCase()}`,
        timeout: 3000,
        position: "topCenter"
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(toggleExpenseStatusFail(error));
    }
  };
};

export const fetchParticipantsAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchParticipantsStart());
    try {
      const response = await getGroupUser(groupId);

      dispatch(
        fetchParticipantsSuccess({
          list: response.data.get_group_users || [],
          total: response.data.get_group_users?.length || 0
        })
      );
    } catch (error) {
      dispatch(fetchParticipantsFail(error));
    }
  };
};

export const fetchExpenseParticipantsAction = (expenseId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchExpenseParticipantsStart());
    try {
      const response = await getExpenseParticipants(expenseId);

      const participants = response.data?.get_expense_users?.map((participant: any) => ({
        display_id: participant?.display_id,
        email: participant?.email,
        is_active: participant?.is_active ?? false,
        user_id: participant?.user_id,
        current_organisation_role: participant?.current_organisation_role,
        attributes: {
          first_name: participant?.attributes?.first_name,
          last_name: participant?.attributes?.last_name
        }
      }));

      dispatch(
        fetchExpenseParticipantsSuccess({
          list: participants,
          total: participants.length
        })
      );
    } catch (error) {
      dispatch(fetchExpenseParticipantsFail(error));
    }
  };
};

export const connectUserToExpenseAction = (request: ConnectUserToExpenseRequest, onSuccess?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(connectUserToExpenseStart());
    try {
      await connectUserToExpense(request);

      dispatch(connectUserToExpenseSuccess());
      immediateToast("success", {
        message: "User connected to claim successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(connectUserToExpenseFail(error));
    }
  };
};

export const removeUserFromExpenseAction = (request: RemoveUserFromExpenseRequest, onSuccess?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(removeUserFromExpenseStart());
    try {
      await removeUserFromExpense(request);

      dispatch(removeUserFromExpenseSuccess());
      immediateToast("success", {
        message: "User removed from claim successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(removeUserFromExpenseFail(error));
    }
  };
};

export const fetchExpenseNotesAction = (expenseId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchExpenseNotesStart());
    try {
      const response = await listNote(expenseId);

      const formattedNotes = response.data?.list_notes?.note.map((note: any) => ({
        noteId: note?.note_id,
        message: note?.payload?.message,
        updated_at: note?.updated_at,
        created_by: note?.created_by
      }));

      dispatch(
        fetchExpenseNotesSuccess({
          list: formattedNotes,
          total: response.data?.list_notes?.note.length
        })
      );
    } catch (error) {
      dispatch(fetchExpenseNotesFail(error));
    }
  };
};

export const createExpenseNoteAction = (data: CreateNoteRequest, onSuccess?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createExpenseNoteStart());
    try {
      await createNote(data);
      dispatch(createExpenseNoteSuccess());
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(createExpenseNoteFail(error));
    }
  };
};

export const fetchTransactionsAction = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTransactionsStart());
    try {
      const response = await getAllTransaction(data);

      const structuredResponse = response.data.search_activity.activity_listing.map((transaction: any) => ({
        id: transaction.activity_id,
        name: transaction.activity_name,
        endDate: transaction.end_timestamp,
        startDate: transaction.start_timestamp,
        ownerId: transaction.owner_id,
        activityStatus: transaction.activity_status,
        receipt_available: transaction.receipt_available,
        approval_number: transaction.approval_number,
        data: JSON.parse(transaction?.data || "{}"),
        xero_details: transaction?.xero_details,
        verified: transaction?.attributes?.verified,
        tagged_users: transaction?.attributes?.tagged_users
      }));

      dispatch(
        fetchTransactionsSuccess({
          list: structuredResponse,
          total: response.data.search_activity.total_size
        })
      );
    } catch (error) {
      dispatch(fetchTransactionsFail(error));
    }
  };
};

export const fetchGroupWalletsAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupWalletsStart());
    try {
      const response = await getGroupDetail(groupId);
      const wallets = response.data?.get_group?.wallets ?? [];

      dispatch(
        fetchGroupWalletsSuccess({
          list: wallets,
          total: wallets.length
        })
      );
      return {
        name: response.data?.get_group?.name,
        description: response.data?.get_group?.description,
        type: response.data?.get_group?.group_type
      };
    } catch (error) {
      dispatch(fetchGroupWalletsFail(error));
    }
  };
};

export const fetchUserWalletsAction = (userId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUserWalletsStart(userId));
    try {
      const request: SearchWalletRequest = {
        from: 0,
        size: 50,
        walletFilter: {
          wallet_status: "ACTIVE",
          user_ids: [userId]
        }
      };

      const response = await searchWallet(request);

      const wallets = response.data?.search_wallet?.items?.map((wallet: any) => ({
        wallet_id: wallet?.wallet_id,
        name: wallet?.name,
        description: wallet?.description,
        wallet_status: wallet?.wallet_status,
        wallet_type: wallet?.wallet_type,
        balance: wallet?.balance ?? 0,
        display_id: wallet?.display_id
      }));

      dispatch(
        fetchUserWalletsSuccess({
          userId,
          list: wallets
        })
      );
    } catch (error) {
      dispatch(
        fetchUserWalletsFail({
          userId,
          error
        })
      );
    }
  };
};

export const groupWalletTransferAction = (
  {
    expenseId,
    sources,
    destinations
  }: {
    expenseId: string;
    sources: TransferRow[];
    destinations: { walletId: string; amount: number }[];
  },
  onSuccess?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(groupWalletTransferStart());
    try {
      const request: GroupWalletTransferRequest = {
        expense_id: expenseId,
        sources: sources.map((source) => ({
          wallet_id: source.walletId,
          amount: Number(source.amount)
        })),
        destinations: destinations.map((destination) => ({
          wallet_id: destination.walletId,
          amount: destination.amount
        }))
      };

      await groupWalletTransfer(request);

      for (const source of sources) {
        connectUserToExpense({
          expense_id: expenseId,
          user_id: source.userId,
          amount: Number(source.amount)
        });
      }

      dispatch(groupWalletTransferSuccess());
      immediateToast("success", {
        message: "Wallet transferred successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(groupWalletTransferFail(error));
    }
  };
};

export const fetchUsersAction = (data: SearchUserRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUsersStart());
    try {
      const response = await searchUser(data);

      const structuredResponse = response.data.search_user.user_listing.map((user: any) => ({
        id: user?.display_id,
        firstName: user?.attributes?.first_name,
        lastName: user?.attributes?.last_name,
        email: user?.email,
        role: user?.current_organisation_role,
        status: user?.is_active,
        userId: user?.user_id,
        createdAt: user?.created_at,
        fullName: formatFullName(user?.attributes?.first_name, user?.attributes?.last_name),
        currentOrganisationRole: user?.current_organisation_role
      }));

      dispatch(
        fetchUsersSuccess({
          list: structuredResponse,
          total: response.data.search_user.total_size
        })
      );
    } catch (error) {
      dispatch(fetchUsersFail(error));
    }
  };
};

export const saveActivityPreferenceAction = (data: SaveActivityPreferenceRequest, onSuccess?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveActivityPreferenceStart());
    try {
      await saveActivityPreference(data);
      dispatch(saveActivityPreferenceSuccess());
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(saveActivityPreferenceFail(error));
    }
  };
};

export default expenseDetailSlice.reducer;
